import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { PageHeader } from "@/components/ui/page-header"
import OnboardingBanner from "@/components/OnboardingBanner"
import { Button } from "@/components/ui/button"
import { StatCard } from "@/components/ui/stat-card"
import { StatusBadge } from "@/components/ui/status-badge"
import { Link } from "react-router-dom"
import { 
  MessageSquare, 
  TrendingUp, 
  DollarSign, 
  Clock, 
  Send, 
  FileText, 
  RefreshCw,
  AlertCircle,
  CheckCircle,
  XCircle,
  Eye,
  CreditCard,
  PenTool
} from "lucide-react"
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts'
import { format } from 'date-fns'
import { formatCurrency, formatNumber } from "@/lib/utils"

// Mock data - 实际项目中应该从API获取
const mockStats = {
  todaySent: 1234,
  todaySuccess: 1198,
  todayFailed: 36,
  monthSent: 45678,
  monthSuccess: 44321,
  successRate: 97.1,
  balance: 8888.88,
  costToday: 123.45,
  costMonth: 4567.89,
}

// 账户信息
const mockAccountInfo = {
  tenantName: "示例科技有限公司",
  balance: 8888.88,
  creditLimit: 10000,
  packageInfo: {
    name: "企业版套餐",
    totalSms: 100000,
    usedSms: 45678,
    expireAt: "2024-12-31"
  },
  status: "ACTIVE" as const
}

// 最新动态
const mockRecentActivities = [
  {
    id: 1,
    type: "template_approved",
    title: "模板审核通过",
    description: "您的验证码模板已审核通过",
    time: "2024-01-15 14:30",
    status: "success"
  },
  {
    id: 2,
    type: "signature_pending",
    title: "签名审核中",
    description: "您的企业签名正在审核中",
    time: "2024-01-15 10:20",
    status: "pending"
  },
  {
    id: 3,
    type: "balance_low",
    title: "余额不足提醒",
    description: "账户余额低于1000元，请及时充值",
    time: "2024-01-14 16:45",
    status: "warning"
  }
]

// 快捷操作
const quickActions = [
  {
    title: "快速发送",
    description: "发送单条或批量短信",
    icon: Send,
    href: "/send",
    color: "bg-blue-500"
  },
  {
    title: "申请模板",
    description: "创建新的短信模板",
    icon: FileText,
    href: "/templates",
    color: "bg-green-500"
  },
  {
    title: "申请签名",
    description: "创建新的短信签名",
    icon: PenTool,
    href: "/signatures",
    color: "bg-purple-500"
  },
  {
    title: "账户充值",
    description: "为账户充值余额",
    icon: CreditCard,
    href: "/account",
    color: "bg-orange-500"
  }
]

const mockTrendData = [
  { date: '2024-01-01', sent: 1200, success: 1150, failed: 50 },
  { date: '2024-01-02', sent: 1100, success: 1080, failed: 20 },
  { date: '2024-01-03', sent: 1300, success: 1250, failed: 50 },
  { date: '2024-01-04', sent: 1400, success: 1380, failed: 20 },
  { date: '2024-01-05', sent: 1234, success: 1198, failed: 36 },
]

const mockPieData = [
  { name: '验证码', value: 60, color: '#8884d8' },
  { name: '通知', value: 30, color: '#82ca9d' },
  { name: '营销', value: 10, color: '#ffc658' },
]

export default function Overview() {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-500" />
      case 'warning':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />
      case 'pending':
        return <Clock className="h-4 w-4 text-blue-500" />
      default:
        return <XCircle className="h-4 w-4 text-red-500" />
    }
  }

  return (
    <div className="space-y-6">
      <OnboardingBanner />
      <PageHeader 
        title="概览"
        description="查看您的账户概况和使用统计"
        action={
          <>
            <Button variant="outline" size="sm" asChild>
              <Link to="/send">
                <Send className="h-4 w-4 mr-2" />
                快速发送
              </Link>
            </Button>
            <Button variant="outline" size="sm">
              <RefreshCw className="h-4 w-4 mr-2" />
              刷新数据
            </Button>
          </>
        }
      />

      {/* 账户信息卡片 */}
      <Card className="bg-gradient-to-r from-blue-500 to-purple-600 text-white">
        <CardHeader>
          <CardTitle className="text-white">账户概况</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold">¥{mockStats.balance}</div>
              <div className="text-sm opacity-90">账户余额</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">{mockStats.monthSent.toLocaleString()}</div>
              <div className="text-sm opacity-90">本月发送</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">{mockStats.successRate}%</div>
              <div className="text-sm opacity-90">成功率</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">¥{mockStats.costMonth}</div>
              <div className="text-sm opacity-90">本月消费</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 今日统计 */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="今日发送"
          value={formatNumber(mockStats.todaySent)}
          description={`成功 ${formatNumber(mockStats.todaySuccess)} / 失败 ${mockStats.todayFailed}`}
          icon={MessageSquare}
          trend={{
            value: 8.2,
            label: "较昨日",
            isPositive: true
          }}
        />

        <StatCard
          title="成功率"
          value={`${mockStats.successRate}%`}
          description="本月平均成功率"
          icon={TrendingUp}
          variant="success"
          trend={{
            value: 0.5,
            label: "较昨日",
            isPositive: true
          }}
        />

        <StatCard
          title="今日消费"
          value={formatCurrency(mockStats.costToday)}
          description={`平均 ${formatCurrency(mockStats.costToday / mockStats.todaySent)}/条`}
          icon={DollarSign}
        />

        <StatCard
          title="待处理事项"
          value="3"
          description="模板审核、签名审核"
          icon={Clock}
          variant="warning"
        />
      </div>

      {/* 最新动态和快捷操作 */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* 最新动态 */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              最新动态
              <Button variant="ghost" size="sm" asChild>
                <Link to="/notifications">
                  <Eye className="h-4 w-4 mr-1" />
                  查看全部
                </Link>
              </Button>
            </CardTitle>
            <CardDescription>系统通知和审核状态更新</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {mockRecentActivities.map((activity) => (
                <div key={activity.id} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="flex-shrink-0 mt-1">
                    <StatusBadge 
                      status={activity.status as any} 
                      size="sm"
                      showIcon={true}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{activity.title}</p>
                    <p className="text-sm text-gray-500">{activity.description}</p>
                    <p className="text-xs text-gray-400 mt-1">{activity.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* 快捷操作 */}
        <Card>
          <CardHeader>
            <CardTitle>快捷操作</CardTitle>
            <CardDescription>常用功能快速入口</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              {quickActions.map((action, index) => (
                <Link
                  key={index}
                  to={action.href}
                  className="flex flex-col items-center p-4 rounded-lg border hover:bg-gray-50 transition-colors group"
                >
                  <div className={`p-3 rounded-full ${action.color} text-white mb-3 group-hover:scale-110 transition-transform`}>
                    <action.icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-medium text-gray-900 mb-1 text-center">{action.title}</h3>
                  <p className="text-xs text-gray-500 text-center">{action.description}</p>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 图表区域 */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-7">
        <Card className="col-span-4">
          <CardHeader>
            <CardTitle>发送趋势</CardTitle>
            <CardDescription>最近7天的短信发送情况</CardDescription>
          </CardHeader>
          <CardContent className="pl-2">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={mockTrendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(value) => format(new Date(value), 'MM/dd')}
                />
                <YAxis />
                <Tooltip 
                  labelFormatter={(value) => format(new Date(value), 'yyyy-MM-dd')}
                />
                <Line 
                  type="monotone" 
                  dataKey="sent" 
                  stroke="#8884d8" 
                  strokeWidth={2}
                  name="发送量"
                />
                <Line 
                  type="monotone" 
                  dataKey="success" 
                  stroke="#82ca9d" 
                  strokeWidth={2}
                  name="成功量"
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        
        <Card className="col-span-3">
          <CardHeader>
            <CardTitle>短信类型分布</CardTitle>
            <CardDescription>按类型统计本月发送情况</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={mockPieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {mockPieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="mt-4 space-y-2">
              {mockPieData.map((item, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div 
                      className="w-3 h-3 rounded-full mr-2" 
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="text-sm">{item.name}</span>
                  </div>
                  <span className="text-sm font-medium">{item.value}%</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 快捷操作 */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="cursor-pointer hover:shadow-md transition-shadow">
          <CardHeader className="text-center">
            <MessageSquare className="h-8 w-8 mx-auto text-blue-500" />
            <CardTitle className="text-lg">发送短信</CardTitle>
            <CardDescription>快速发送单条或批量短信</CardDescription>
          </CardHeader>
        </Card>
        
        <Card className="cursor-pointer hover:shadow-md transition-shadow">
          <CardHeader className="text-center">
            <FileText className="h-8 w-8 mx-auto text-green-500" />
            <CardTitle className="text-lg">申请模板</CardTitle>
            <CardDescription>创建新的短信模板</CardDescription>
          </CardHeader>
        </Card>
        
        <Card className="cursor-pointer hover:shadow-md transition-shadow">
          <CardHeader className="text-center">
            <TrendingUp className="h-8 w-8 mx-auto text-purple-500" />
            <CardTitle className="text-lg">查看报表</CardTitle>
            <CardDescription>详细的数据统计分析</CardDescription>
          </CardHeader>
        </Card>
        
        <Card className="cursor-pointer hover:shadow-md transition-shadow">
          <CardHeader className="text-center">
            <DollarSign className="h-8 w-8 mx-auto text-orange-500" />
            <CardTitle className="text-lg">账户充值</CardTitle>
            <CardDescription>为账户充值余额</CardDescription>
          </CardHeader>
        </Card>
      </div>
    </div>
  )
}